import { useContentfulData } from "@ubif/components";
import { EntryFieldTypes, EntrySkeletonType } from "contentful";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import styled from "styled-components";

type ReleaseNotesSkeleton = EntrySkeletonType<
  {
    title: EntryFieldTypes.Symbol;
    version: EntryFieldTypes.Symbol;
    releaseDate: EntryFieldTypes.Date;
    summary: EntryFieldTypes.Symbol;
    body: EntryFieldTypes.Text;
  },
  "releaseNotes"
>;

const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 52rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: var(--aui-on-surface);
`;

const NoteCard = styled.article`
  border: 1px solid var(--aui-outline-soft);
  border-radius: var(--aui-border-radius-100);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  background: var(--aui-surface);
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.25rem;
`;

const NoteTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--aui-on-surface);
  margin: 0;
`;

const VersionBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  background: var(--aui-primary-soft);
  color: var(--aui-primary);
  border-radius: var(--aui-border-radius-round);
  padding: 0.25rem 0.75rem;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const ReleaseDate = styled.time`
  font-size: 0.875rem;
  color: var(--aui-on-surface-soft);
  display: block;
  margin-bottom: 0.75rem;
`;

const Summary = styled.p`
  font-size: 0.875rem;
  color: var(--aui-on-surface-soft);
  margin: 0 0 1rem;
  font-style: italic;
`;

const MarkdownBody = styled.div`
  font-size: 1rem;
  color: var(--aui-on-surface);
  line-height: 1.3;

  h1,
  h2,
  h3 {
    font-weight: 700;
    margin: 1rem 0 0.5rem;
  }

  ul,
  ol {
    padding-left: 1.5rem;
    margin: 0.5rem 0;
  }

  li {
    margin-bottom: 0.25rem;
  }

  code {
    background: var(--aui-surface-variant);
    border-radius: 0.25rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.75rem;
  }

  p {
    margin: 0.5rem 0;
  }
`;

const StatusMessage = styled.p`
  color: var(--aui-on-surface-soft);
`;

export const ReleaseNotesPage = () => {
  const { t, i18n } = useTranslation("root-config");

  const { data, isLoading, isError } = useContentfulData<ReleaseNotesSkeleton>({
    content_type: "releaseNotes"
  });

  const sorted = [...(data ?? [])].sort(
    (a, b) => new Date(b.fields.releaseDate).getTime() - new Date(a.fields.releaseDate).getTime()
  );

  return (
    <PageWrapper>
      <PageTitle>{t("ReleaseNotes.title")}</PageTitle>
      {(isLoading || data === null) && !isError && <StatusMessage>{t("ReleaseNotes.loading")}</StatusMessage>}
      {isError && <StatusMessage>{t("ReleaseNotes.error")}</StatusMessage>}
      {!isLoading && data !== null && !isError && sorted.length === 0 && (
        <StatusMessage>{t("ReleaseNotes.empty")}</StatusMessage>
      )}
      {sorted.map((entry) => {
        const { title, version, releaseDate, summary, body } = entry.fields;
        return (
          <NoteCard key={entry.sys.id}>
            <NoteHeader>
              <NoteTitle>{title}</NoteTitle>
              <VersionBadge>{version}</VersionBadge>
            </NoteHeader>
            <ReleaseDate dateTime={releaseDate}>
              {new Date(releaseDate).toLocaleDateString(i18n.language, {
                year: "numeric",
                month: "long",
                day: "numeric"
              })}
            </ReleaseDate>
            {summary && <Summary data-testid="release-note-summary">{summary}</Summary>}
            <MarkdownBody>
              <ReactMarkdown>{body}</ReactMarkdown>
            </MarkdownBody>
          </NoteCard>
        );
      })}
    </PageWrapper>
  );
};
